import { loader } from 'graphql.macro'
import get from 'lodash/get'
import difference from 'lodash/difference'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import includes from 'lodash/includes'
import map from 'lodash/map'
import isFinite from 'lodash/isFinite'

import { fetchFieldsByIds } from './fields'
import { fetchGraphqlWrapper, fetchRequestWrapper, setRequestEnd } from './ui'
import { getToken } from '../userStoredData'

import {
  hasLegendsLoaded,
  getSelectedLayer,
  getTouchedLayers,
  getKeepSelection,
  getDefaultLayerId,
  getFilteredFieldIds,
  getGlobalMapFeatures,
  getSelectedLayerLegend
} from '../selectors/globalLayer'
import { getFieldsIds } from '../selectors/fields'
import { getConfigProjection } from '../selectors/config'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { GLOBAL_LAYERS_FRONTEND_COLOR_STRATEGY, DEFAULT_LEGEND_TYPE, NO_VALUE } from '@layers-frontend/commons/constants'
import { getUserRoles, hasRoleOrderedFeatures } from '@layers-frontend/commons/store/selectors/user'

export const TOGGLE_LEGEND = 'TOGGLE_LEGEND'

export const TOGGLE_GLOBAL_LAYER_LEGEND = 'TOGGLE_GLOBAL_LAYER_LEGEND'
export const SELECT_GLOBAL_LAYER = 'SELECT_GLOBAL_LAYER'

export const REQUEST_GLOBAL_LAYER_BY_LEGEND = 'REQUEST_GLOBAL_LAYER_BY_LEGEND'
export const RECEIVE_GLOBAL_LAYER_BY_LEGEND = 'RECEIVE_GLOBAL_LAYER_BY_LEGEND'

export const REQUEST_GLOBAL_LAYER_LEGEND_BY_TYPE_ID = 'REQUEST_GLOBAL_LAYER_LEGEND_BY_TYPE_ID'
export const RECEIVE_GLOBAL_LAYER_LEGEND_BY_TYPE_ID = 'RECEIVE_GLOBAL_LAYER_LEGEND_BY_TYPE_ID'

export const REQUEST_GLOBAL_LAYER_LEGEND_BY_DELIVERABLE_NAME = 'REQUEST_GLOBAL_LAYER_LEGEND_BY_DELIVERABLE_NAME'
export const RECEIVE_GLOBAL_LAYER_LEGEND_BY_DELIVERABLE_NAME = 'RECEIVE_GLOBAL_LAYER_LEGEND_BY_DELIVERABLE_NAME'

export const UPDATE_SEQUENTIAL_LEGEND = 'UPDATE_SEQUENTIAL_LEGEND'
export const SET_HIGHLIGHTED_CATEGORY = 'SET_HIGHLIGHTED_CATEGORY'

export const TOGGLE_SEQUENTIAL_NULLS = 'TOGGLE_SEQUENTIAL_NULLS'

export const TOGGLE_SELECTION = 'TOGGLE_SELECTION'
export const SET_FILTERED_FIELD_IDS = 'SET_FILTERED_FIELD_IDS'

export const TOGGLE_VISIBILITY = 'TOGGLE_VISIBILITY'
export const TOGGLE_KEEP_SELECTION = 'TOGGLE_KEEP_SELECTION'

export const RESET_GLOBAL_LEGEND = 'RESET_GLOBAL_LEGEND'

export const REQUEST_GLOBAL_MAP_CENTROID = 'REQUEST_GLOBAL_MAP_CENTROID'
export const RECEIVE_GLOBAL_MAP_CENTROID = 'RECEIVE_GLOBAL_MAP_CENTROID'

export const resetGlobalLegend = legend => dispatch => {
  dispatch({
    type: RESET_GLOBAL_LEGEND,
    legend
  })

  return dispatch(setFilteredFieldIds())
}

export const toggleKeepSelection = () => (dispatch, getState) => {
  const state = getState()
  const selectedLayer = getSelectedLayer(state)
  const touchedLayers = getTouchedLayers(state)

  touchedLayers.forEach(layer => {
    if (layer.baseName !== selectedLayer) {
      dispatch(resetGlobalLegend(layer))
    }
  })

  return dispatch({
    type: TOGGLE_KEEP_SELECTION
  })
}

export const toggleGlobalMapSelection = selectFilteredFields => (dispatch, getState) => {
  const state = getState()
  const fetchedFields = getFieldsIds(state)
  const fieldIds = getFilteredFieldIds(state)

  const unfetchedFields = difference(fieldIds, fetchedFields)
  if (!isEmpty(unfetchedFields)) {
    dispatch(fetchFieldsByIds(unfetchedFields))
  }
  return dispatch({
    type: TOGGLE_SELECTION,
    selectFilteredFields,
    fieldIds
  })
}

export const setFilteredFieldIds = () => (dispatch, getState) => {
  const state = getState()
  const features = getGlobalMapFeatures(state)
  const projection = getConfigProjection(state)
  const shouldOrderFeatures = hasRoleOrderedFeatures(state)
  const selectedLayerLegend = getSelectedLayerLegend(state)
  const touchedLegends = getTouchedLayers(state)
  const keepSelection = getKeepSelection(state)
  const selectedLayerKey = get(selectedLayerLegend, 'baseName')
  const selectedLayerLegendValues = get(selectedLayerLegend, 'legend')

  const sortedFeatures = shouldOrderFeatures ? orderBy(features, ['properties.gm_name'], 'desc') : features
  const isScaleFromDB = get(selectedLayerLegend, 'isScaleFromDB')
  const isScaleTypeCategoric = get(selectedLayerLegend, 'isScaleTypeCategoric')
  const scale = get(selectedLayerLegend, 'scale')
  const value = get(selectedLayerLegend, 'legend.value')
  const showNulls = get(selectedLayerLegend, 'legend.showNulls')
  const disabledLegends = []

  if (!isScaleFromDB && isScaleTypeCategoric) {
    map(selectedLayerLegendValues, legend => {
      if (!legend.visible) disabledLegends.push(legend.value)
    })
  }
  const selectedLayers = []

  const filteredFeatures = map(sortedFeatures, (feature, index) => {
    // A field without polygon geometry is valid, but it cannot be filtered, coloured or
    // identified as a map feature. Skip it defensively so a malformed feature (one with no
    // properties, hence an undefined gm_field_id) never reaches filteredFeatures / filteredFieldIds.
    if (!feature || !feature.properties) {
      return null
    }

    let fill = themeColors.lightGreySolid

    const fieldId = get(feature, ['properties', 'gm_field_id'])
    const selectedLayerValue = get(feature, ['properties', selectedLayerKey])
    // checks if the current feature has any properties that have been touched
    const filters = map(touchedLegends, touchedLegend => {
      const name = get(touchedLegend, 'baseName')
      const isScaleFromDB = get(touchedLegend, 'isScaleFromDB')
      const isScaleTypeCategoric = get(touchedLegend, 'isScaleTypeCategoric')
      const sequentialRange = get(touchedLegend, 'legend.value') // sequential
      const showNulls = get(touchedLegend, 'legend.showNulls') // sequential
      const legend = get(touchedLegend, 'legend')
      const touchedLegendValue = get(feature, ['properties', name])

      if (!isScaleFromDB && isScaleTypeCategoric) {
        const isInIndex = legend.findIndex(l => l.value === touchedLegendValue)
        return isInIndex !== -1 && legend[isInIndex].visible
      }
      if (!isScaleFromDB && !isScaleTypeCategoric) {
        if (showNulls && touchedLegendValue === null) return true
        if (!showNulls && touchedLegendValue === null) return false

        return touchedLegendValue != null && sequentialRange && touchedLegendValue >= sequentialRange[0] && touchedLegendValue < sequentialRange[1]
      }
      const colorIndex = legend.findIndex(legend => {
        const range = legend.range
        if (touchedLegendValue == null && range === NO_VALUE) return true
        if (touchedLegendValue != null && range && touchedLegendValue >= range[0] && touchedLegendValue < range[1]) {
          return true
        }
        return false
      })
      return colorIndex !== -1 && legend[colorIndex] && legend[colorIndex].visible
    })

    // if any are false, then the feature needs to be filtered
    const isFiltered = filters.some(f => f === false)

    if (isScaleFromDB) {
      const colorIndex = selectedLayerLegendValues?.findIndex(legend => {
        const range = legend.range

        if (!isFinite(selectedLayerValue) && range === NO_VALUE) return true
        if (isFinite(selectedLayerValue) && range && selectedLayerValue >= range[0] && selectedLayerValue < range[1]) {
          return true
        }
        return false
      })
      const visible = selectedLayerLegendValues?.[colorIndex] && selectedLayerLegendValues[colorIndex].visible

      // for db legends
      // if value falls in a range that has visible set to false, remove it
      // or if keep selection is on, and there are any filtered values, the fature is not visible
      if (!visible || (isFiltered && keepSelection)) {
        return null
      } else {
        selectedLayers.push(fieldId)
        fill = colorIndex !== -1 ? selectedLayerLegendValues[colorIndex].color : themeColors.lightGreySolid
      }
    } else {
      fill = scale && selectedLayerValue != null ? scale(selectedLayerValue) : themeColors.lightGreySolid

      // for categorical legends
      // if value is included in the disabled legends, the feature is not visible
      // if it isnt, add field to selected layers
      if (disabledLegends.includes(selectedLayerValue) || (isFiltered && keepSelection)) {
        return null
      } else if (isScaleTypeCategoric) {
        selectedLayers.push(fieldId)
      }
      // for sequential legends
      // if value is not inside selected range, the feature is not visible
      // or if keep selection is on, and there are any filtered values, the feature is not visible
      if ((selectedLayerValue != null && value && (selectedLayerValue < value[0] || selectedLayerValue > value[1])) || (isFiltered && keepSelection)) {
        return null
      } else if (!isScaleTypeCategoric) {
        if (showNulls && selectedLayerValue == null) {
          selectedLayers.push(fieldId)
        } else if (selectedLayerValue != null) {
          selectedLayers.push(fieldId)
        }
        if (selectedLayerValue == null && !showNulls) {
          return null
        }
      }
    }
    if (!feature.geometry) {
      return null
    }
    return {
      index,
      field: { ...feature.properties },
      properties: { ...feature.properties },
      type: feature.type,
      style: {
        stroke: {
          width: 2,
          color: themeColors.whiteColor
        },
        fill: {
          color: fill
        }
      },
      projection,
      coordinates: feature.geometry.coordinates,
      geometry: feature.geometry
    }
  })

  return dispatch({
    type: SET_FILTERED_FIELD_IDS,
    filteredFieldIds: selectedLayers,
    filteredFeatures
  })
}

export const setHighlightedCategory = highlightedCategory => ({
  type: SET_HIGHLIGHTED_CATEGORY,
  highlightedCategory
})

export const toggleSequentialLegendNulls = newValue => (dispatch, getState) => {
  const state = getState()
  const selectedLayer = getSelectedLayer(state)
  dispatch({
    type: TOGGLE_SEQUENTIAL_NULLS,
    newValue,
    selectedLayer
  })
  return dispatch(setFilteredFieldIds())
}

export const updateSequentialLegend = newValue => (dispatch, getState) => {
  const state = getState()
  const selectedLayer = getSelectedLayer(state)

  dispatch({
    type: UPDATE_SEQUENTIAL_LEGEND,
    newValue,
    selectedLayer
  })
  return dispatch(setFilteredFieldIds())
}

export const toggleLegend = legend => dispatch => {
  dispatch({
    type: TOGGLE_LEGEND,
    legend
  })
  return dispatch(setFilteredFieldIds())
}

/**
 * Fetches the global layer by legend.
 *
 * @returns {Function} A function that dispatches the response.
 */
export const fetchGlobalLayerByLegend = () => (dispatch, getState) => {
  const userRoles = getUserRoles(getState())
  const legendsAvailable = GLOBAL_LAYERS_FRONTEND_COLOR_STRATEGY.filter(
    ({ necessaryRoles }) => !necessaryRoles || necessaryRoles.some(role => userRoles.includes(role))
  ).map(layer => layer.id)

  const response = fetchGlobalMap({
    mode: 'legend',
    legendsAvailable,
    returnType: RECEIVE_GLOBAL_LAYER_BY_LEGEND,
    requestType: REQUEST_GLOBAL_LAYER_BY_LEGEND
  })

  return dispatch(response)
}

export const fetchGlobalLayerByDeliverableName = () => dispatch => {
  const defaultLayer = DEFAULT_LEGEND_TYPE
  return dispatch(fetchByDeliverableName(defaultLayer))
}

/**
 * Fetches the legend of a global layer based on the given base filename.
 *
 * @param {string} basefilename - The base filename of the global layer.
 * @returns {Function} - The dispatch function.
 */
export const fetchByDeliverableName = basefilename => dispatch => {
  const response = fetchGlobalMap({
    basefilename,
    mode: 'deliverable_legend',
    returnType: RECEIVE_GLOBAL_LAYER_LEGEND_BY_DELIVERABLE_NAME,
    requestType: REQUEST_GLOBAL_LAYER_LEGEND_BY_DELIVERABLE_NAME
  })

  dispatch(response)
  dispatch({
    type: SELECT_GLOBAL_LAYER,
    selectedLayer: basefilename
  })
  return dispatch(setFilteredFieldIds())
}

export const toggleGlobalLayerLegend = () => ({
  type: TOGGLE_GLOBAL_LAYER_LEGEND
})

export const toggleGlobalLayersVisibility = toggle => dispatch => {
  dispatch({
    type: TOGGLE_VISIBILITY,
    toggle
  })
  return dispatch(setFilteredFieldIds())
}

export const selectGlobalLayer = selectedLayer => (dispatch, getState) => {
  const state = getState()

  const isFrontendColorStrategy = includes(map(GLOBAL_LAYERS_FRONTEND_COLOR_STRATEGY, 'id'), selectedLayer)

  if (!isFrontendColorStrategy) {
    const selectedLayerHasLegendsLoaded = hasLegendsLoaded(selectedLayer)(state)
    if (!selectedLayerHasLegendsLoaded) dispatch(fetchByDeliverableName(selectedLayer))

    if (selectedLayerHasLegendsLoaded) {
      dispatch({
        type: SELECT_GLOBAL_LAYER,
        selectedLayer
      })
      return dispatch(setFilteredFieldIds())
    }
  }

  if (isFrontendColorStrategy) {
    dispatch({
      type: SELECT_GLOBAL_LAYER,
      selectedLayer
    })
    return dispatch(setFilteredFieldIds())
  }
}

export const receiveGlobalLayerLegendByTypeId = ({ title, legend }) => ({
  type: RECEIVE_GLOBAL_LAYER_LEGEND_BY_TYPE_ID,
  title,
  legend
})

const getGlobalLayerLegendByTypeIdQuery = loader('../graphql/getGlobalLayerLegendByTypeId.gql').loc.source.body

export const fetchDefaultGlobalLayerLegendByTypeId = () => (dispatch, getState) => {
  const defaultLayerId = getDefaultLayerId(getState()) || DEFAULT_LEGEND_TYPE
  return dispatch(fetchGlobalLayerLegendByTypeId(defaultLayerId))
}

export const fetchGlobalLayerLegendByTypeId = typeId => dispatch =>
  dispatch(
    fetchGraphqlWrapper({
      requestType: REQUEST_GLOBAL_LAYER_LEGEND_BY_TYPE_ID,
      query: getGlobalLayerLegendByTypeIdQuery,
      variables: { typeId },
      onSuccess: ({ title, legend }) => {
        return receiveGlobalLayerLegendByTypeId({ title, legend })
      }
    })
  )

export const fetchGlobalMapCentroids = () => dispatch => {
  dispatch(
    fetchCentroids({
      dispatch,
      overlay: true,
      mode: 'centroid',
      requestType: REQUEST_GLOBAL_MAP_CENTROID,
      returnType: RECEIVE_GLOBAL_MAP_CENTROID
    })
  )
}

const fetchCentroids = ({ authorizingRole = null, overlay = false, requestType, returnType, mode, nextToken, queryId, nextQueryData = [], dispatch }) => {
  const baseCustomRoute = `${window.location.origin}/athena/global_maps`
  const body = nextToken
    ? { mode, next_token: nextToken, api_key: getToken(), query_id: queryId }
    : {
        api_key: getToken(),
        mode
      }
  return fetchRequestWrapper({
    customRoute: `${baseCustomRoute}?${new URLSearchParams({ api_key: getToken() })}`,
    fetchOptions: {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' }
    },
    overlay,
    requestType,
    authorizingRole,
    hasNextPage: !!nextToken,
    onSuccess: data => {
      if (data.next_token) {
        return fetchCentroids({
          authorizingRole,
          overlay,
          requestType,
          returnType,
          mode,
          nextToken: data.next_token,
          apiKey: getToken(),
          queryId: data.query_id,
          nextQueryData: [...data.data, ...nextQueryData],
          dispatch
        })
      }
      dispatch(setRequestEnd(requestType))
      const response = { type: returnType, data: [...data.data, ...nextQueryData] }
      return response
    }
  })
}

const fetchGlobalMap = ({ authorizingRole = null, overlay = false, requestType, returnType, mode, legendsAvailable, basefilename }) => {
  const baseCustomRoute = `${window.location.origin}/athena/global_maps`
  return fetchRequestWrapper({
    customRoute: `${baseCustomRoute}?${new URLSearchParams({ api_key: getToken() })}`,
    fetchOptions: {
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        mode,
        api_key: getToken()
      }),
      method: 'POST'
    },
    requestType,
    authorizingRole,
    overlay,
    onSuccess: data => {
      const response = { type: returnType, data: data?.data ? data.data : data }

      legendsAvailable && (response.legendsAvailable = legendsAvailable)
      basefilename && (response.basefilename = basefilename)

      return response
    }
  })
}
